<template>
  <section v-if="isVisible" ref="containerRef" class="preloader-counter">
    <!-- Center Counter -->
    <div class="preloader-counter__center">
      <div class="preloader-counter__number">
        <span ref="percentRef" class="preloader-counter__percent">000</span>
        <span ref="symbolRef" class="preloader-counter__symbol">%</span>
      </div>
      <div ref="labelRef" class="preloader-counter__label">
        Loading Experience
      </div>
    </div>

    <!-- Bottom Progress Bar -->
    <div class="preloader-counter__bar-track">
      <div ref="barRef" class="preloader-counter__bar-fill"></div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { gsap } from 'gsap/dist/gsap.js'

const containerRef = ref(null)
const percentRef = ref(null)
const symbolRef = ref(null)
const barRef = ref(null)
const labelRef = ref(null)
const isVisible = ref(true)

const emit = defineEmits(['complete'])

onMounted(() => {
  const screenElement = document.getElementById('screen')
  if (screenElement) {
    screenElement.style.overflow = 'hidden'
  }

  if (!containerRef.value) return

  const tl = gsap.timeline({
    onComplete: () => {
      isVisible.value = false
      const screenElement = document.getElementById('screen')
      if (screenElement) {
        screenElement.style.overflow = ''
      }
      window.dispatchEvent(new CustomEvent('preloader-complete'))
      emit('complete')
    }
  })

  // 1. Counter & Bar Animation
  const counter = { value: 0 }

  tl.to(counter, {
    value: 100,
    duration: 2.5,
    ease: "power2.inOut",
    onUpdate: () => {
      if (percentRef.value) {
        percentRef.value.textContent = Math.round(counter.value).toString().padStart(3, '0')
      }
    }
  })
  .to(barRef.value, {
    width: "100%",
    duration: 2.5,
    ease: "power2.inOut"
  }, "<")

  // 2. Exit Sequence - Fade out content
  tl.to([percentRef.value, symbolRef.value, labelRef.value, barRef.value], {
    opacity: 0,
    y: -20,
    duration: 0.5,
    ease: "power2.in",
    stagger: 0.1
  })

  // 3. Slide up curtain
  tl.to(containerRef.value, {
    yPercent: -100,
    duration: 1.2,
    ease: "expo.inOut",
    delay: -0.1
  })
})
</script>

<style lang="scss">
.preloader-counter {
  position: fixed;
  inset: 0;
  z-index: 500;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #fff;
  color: #000;
  overflow: hidden;
  font-family: var(--font-heading), sans-serif;
}

.preloader-counter__center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.preloader-counter__number {
  display: flex;
  align-items: baseline;
  overflow: hidden;
  font-weight: 700;
  letter-spacing: -0.05em;
  line-height: 1.5;
  padding: 0.25em 0;
}

.preloader-counter__percent {
  font-size: clamp(80px, 15vw, 160px);
  line-height: 1.5;
}

.preloader-counter__symbol {
  font-size: clamp(24px, 4vw, 48px);
  font-weight: 400;
  margin-left: 8px;
}

.preloader-counter__label {
  margin-top: 16px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  opacity: 0.5;
}

.preloader-counter__bar-track {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: rgba(0, 0, 0, 0.1);
}

.preloader-counter__bar-fill {
  position: absolute;
  left: 0;
  top: 0;
  width: 0;
  height: 100%;
  background: #000;
}
</style>
